/**
 * @coeldery/bottom-sheet — 由底部升起嘅**面板**（彈窗；手機友善）
 *
 * 為何係模組：任何「撳一下 → 彈一版嘢出嚟（內容 ＋ 動作）」嘅流程都要同一個外殼
 *   （第二個 app 一定用得到）→ R3 做成模組。
 * ⚠️ 查庫證據（R1，2026-09-22）：庫內 `form-modal` 1.0.0 係「表單對話框」（動作列只有
 *   **取消＋儲存**，而且依賴 `ui-kit`）→ 唔夠本需求（要有〔重新生成〕〔手動編輯〕〔儲存〕
 *   〔開始生成〕〔遲啲先〕等任意動作）；`dialog`／`sheet`／`drawer` 關鍵字 **0 命中**。
 *
 * 血緣：cosingery（2026-09-22 產品負責人 UI／UX 五改；`decisions/023`
 *   「after creating button is clicked, should have a popup page for the 歌詞 …」）
 *
 * 設計（手機優先）：
 *   · 背景遮罩 `var(--overlay-scrim)`；面板由底部升起（圓角上緣）；最高 `maxHeightVh`%（預設 85）
 *   · 標題列（標題 ＋ ✕）／內容區（可捲）／**動作列（`actions` ＝ ReactNode，由父層砌）**
 *   · 可及性：`role="dialog"` ＋ `aria-modal="true"`；ESC 關；點背景關（點內容唔關）
 *   · 開著時**鎖背景捲動**；`window`／`document` 唔存在（SSR）都要安全
 *   · 顏色／圓角一律 CSS var；文字由 props 入（`titleKey` 用 i18n、`title` 用現成文字）
 */
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';

export interface BottomSheetProps {
  open: boolean;
  /** i18n key（有就優先過 `title`） */
  titleKey?: string;
  /** 直接文字（唔想用 i18n 時） */
  title?: string;
  children?: ReactNode;
  /** 動作列（由父層砌：例〔重新生成〕〔手動編輯〕／〔開始生成〕〔遲啲先〕） */
  actions?: ReactNode;
  onClose: () => void;
  /** 面板最高高度（螢幕百分比；預設 85） */
  maxHeightVh?: number;
  /** ✕ 掣嘅 i18n key */
  closeAriaKey?: string;
}

export default function BottomSheet({
  open,
  titleKey,
  title,
  children,
  actions,
  onClose,
  maxHeightVh = 85,
  closeAriaKey = 'sheet_close_aria',
}: BottomSheetProps) {
  /** ESC 關（鍵盤可及性） */
  useEffect(() => {
    if (!open || typeof document === 'undefined') return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  /** 開著時鎖背景捲動（唔鎖 → 手指會捲到下面嘅對話） */
  useEffect(() => {
    if (!open || typeof document === 'undefined') return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const { t } = useTranslation();
  if (!open) return null;
  const heading = titleKey ? t(titleKey) : title;

  return (
    <div
      data-testid="bottom-sheet"
      role="dialog"
      aria-modal="true"
      aria-label={heading ?? undefined}
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        display: 'flex',
        alignItems: 'flex-end',
        background: 'var(--overlay-scrim)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: '720px',
          margin: '0 auto',
          maxHeight: `${maxHeightVh}svh`,
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          padding: '14px 16px',
          paddingBottom: 'calc(14px + env(safe-area-inset-bottom, 0px))',
          borderTopLeftRadius: '22px',
          borderTopRightRadius: '22px',
          background: 'var(--color-card)',
          boxShadow: 'var(--shadow-modal, var(--shadow-soft, none))',
        }}
      >
        {/* 標題列 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <h2
            style={{
              margin: 0,
              flex: '1 1 auto',
              fontSize: 'var(--font-size-title, 24px)',
              fontWeight: 800,
              color: 'var(--color-text)',
            }}
          >
            {heading}
          </h2>
          <button
            type="button"
            data-testid="sheet-close"
            aria-label={t(closeAriaKey)}
            title={t(closeAriaKey)}
            onClick={onClose}
            style={{
              width: '44px',
              height: '44px',
              flex: '0 0 auto',
              borderRadius: '50%',
              border: '1.5px solid var(--color-divider)',
              background: 'var(--color-card)',
              color: 'var(--color-text)',
              fontFamily: 'inherit',
              fontSize: 'var(--font-size-button, 18px)',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            ✕
          </button>
        </div>

        {/* 內容（可捲） */}
        <div style={{ flex: '1 1 auto', overflowY: 'auto', minHeight: 0 }}>{children}</div>

        {/* 動作列（父層砌） */}
        {actions ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>{actions}</div>
        ) : null}
      </div>
    </div>
  );
}
